import { supabase } from '../supabase';
import { fetchAllRowsInChunks } from '../utils/async/fetchAllRowsInChunks';
import { tenantMidnightUtcIso } from '../utils/date/tenantDate';
import { isOperationsSession } from '../utils/auth/currentTenantRoleStore';
import { getFieldReadTables } from '../utils/job/fieldReadTables';
import { resolveBulkWindowStarts } from '../utils/customer/bulkLifecycleBundles';
import { addOneDay } from './lifecycleBundleService';
import { assembleClosedStretchBundles, storeClosedStretchBundles } from '../utils/schedule/closedStretchVisits';
import { JOB_CONTEXT } from '../utils/job/jobContext';

const inflight = new Map();

function markUnknown(customerIds, windowStartByCustomer, windowUnknown) {
  const map = assembleClosedStretchBundles({
    customerIds,
    windowStartByCustomer: windowStartByCustomer || new Map(),
    jobRows: [],
  });
  if (!windowUnknown) return map;
  const flagged = new Map();
  map.forEach((bundle, id) => flagged.set(id, { ...bundle, windowUnknown: true }));
  return flagged;
}

/**
 * Display-only closed-stretch visits. Not the invoice bundle: job_context is
 * off_season, the window is the latest transition TO closed, and billed jobs
 * still count.
 */
export async function fetchClosedStretchBundles({
  tenantId, customerIds, transitionDate, tz = 'UTC',
}) {
  if (!Array.isArray(customerIds) || customerIds.length === 0) return new Map();
  if (isOperationsSession()) return markUnknown(customerIds, new Map(), true);

  const nextDay = addOneDay(transitionDate);
  const { data: changeRows, error: changeErr } = await fetchAllRowsInChunks(customerIds, (chunk, selectOpts) => supabase
    .from('customer_status_changes')
    .select('customer_id, changed_at', selectOpts)
    .eq('tenant_id', tenantId)
    .in('customer_id', chunk)
    .eq('to_status', 'closed')
    .lte('changed_at', tenantMidnightUtcIso(nextDay, tz))
    .order('changed_at', { ascending: false })
    .order('id'));
  if (changeErr) throw new Error(changeErr.message);

  const haveChange = new Set((changeRows ?? []).map((row) => String(row.customer_id)));
  const missing = customerIds.filter((id) => !haveChange.has(String(id)));
  const tables = getFieldReadTables();
  let customerCreatedRows = [];
  if (missing.length) {
    const { data: custRows, error: custErr } = await fetchAllRowsInChunks(missing, (chunk, selectOpts) => supabase
      .from(tables.customers)
      .select('id, created_at', selectOpts)
      .eq('tenant_id', tenantId)
      .in('id', chunk)
      .order('id'));
    if (custErr) throw new Error(custErr.message);
    customerCreatedRows = custRows ?? [];
  }

  const windowStartByCustomer = resolveBulkWindowStarts({
    customerIds, changeRows: changeRows ?? [], customerCreatedRows, tz,
  });
  const windows = [...windowStartByCustomer.values()];
  if (windows.length === 0) return markUnknown(customerIds, windowStartByCustomer, false);

  const earliest = windows.reduce((a, b) => (a < b ? a : b));
  const isBaseJobs = tables.jobs === 'jobs';
  const jobsSelect = isBaseJobs
    ? 'id, customer_id, date, total, unpriced_line_count, visit_reason'
    : 'id, customer_id, date, visit_reason';
  const { data: jobRows, error: jobsErr } = await fetchAllRowsInChunks(customerIds, (chunk, selectOpts) => supabase
    .from(tables.jobs)
    .select(jobsSelect, selectOpts)
    .eq('tenant_id', tenantId)
    .in('customer_id', chunk)
    .eq('job_context', JOB_CONTEXT.OFF_SEASON)
    .gte('date', earliest)
    .lte('date', transitionDate)
    .order('id'));
  if (jobsErr) throw new Error(jobsErr.message);

  return assembleClosedStretchBundles({
    customerIds,
    windowStartByCustomer,
    jobRows: jobRows ?? [],
    transitionDate,
  });
}

/** Deduped fetch shared by the dashboard prefetch and the closed tab. */
export function prefetchClosedStretchBundles({
  tenantId, customerIds, transitionDate, tz = 'UTC',
}) {
  const ids = (customerIds || []).map(String).filter(Boolean);
  if (!tenantId || !transitionDate || ids.length === 0) return Promise.resolve(new Map());
  const key = `${tenantId}:${transitionDate}:${tz}:${[...ids].sort().join(',')}`;
  const existing = inflight.get(key);
  if (existing) return existing;
  const promise = fetchClosedStretchBundles({ tenantId, customerIds: ids, transitionDate, tz })
    .then((result) => {
      storeClosedStretchBundles(tenantId, result);
      return result;
    })
    .finally(() => {
      if (inflight.get(key) === promise) inflight.delete(key);
    });
  inflight.set(key, promise);
  return promise;
}
